import React, { useRef, useState, useEffect } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Container, Text, Row, Col, className, Slider } from 'design-system';
import { useTranslate } from 'gatsby-theme-soluble-source';

import ButtonComponent from '../ButtonComponent';
import TransitionLink from '../TransitionLink';

import { graphql } from 'gatsby';

import style from './style.module.scss';

const sliderSettings = {
    controls: false,
    loop: false,
    nav: true,
    navContainer: '#thumbnails-cos',
    navAsThumbnails: true,
    mouseDrag: true,
    arrowKeys: true,
};

export default function CaseStudiesSection({ mt, dark, posts }) {
    const translation = useTranslate();
    const sliderRef = useRef();

    const [currentSlide, setCurrentSlide] = useState(posts?.length ? 0 : null);

    useEffect(() => {
        if (typeof window === 'undefined' || !sliderRef.current) {
            return;
        }

        const configureCurrentSlide = (slider) => {
            setCurrentSlide(slider.index);
        };

        sliderRef.current.slider.events.on(
            'indexChanged',
            configureCurrentSlide,
        );
        return () => {
            if (sliderRef.current?.slider) {
                sliderRef.current.slider.events.off(
                    'indexChanged',
                    configureCurrentSlide,
                );
            }
        };
    }, [sliderRef, setCurrentSlide]);

    if (!posts?.length) {
        return null;
    }

    return (
        <section
            data-datocms-noindex
            {...className(
                `${mt ? mt : 'mt-10'} ${style.wrapper} ${
                    style[`wrapper--${dark ? 'dark' : 'light'}`]
                }`,
            )}>
            <Container pb={{ xs: 15, lg: 0 }}>
                <Row className="d-lg-flex align-items-lg-center">
                    <Col
                        col={{ xs: 7, lg: 4 }}
                        offset={{ xs: 5, lg: 0 }}
                        order={{ lg: 2 }}>
                        {posts?.length ? (
                            <div {...className(style['image-wrapper'])}>
                                {posts[currentSlide]?.featuredImage
                                    ?.gatsbyImageData ? (
                                    <GatsbyImage
                                        {...className(
                                            `img-fluid shadow-lg ${style.image}`,
                                        )}
                                        alt={
                                            posts[currentSlide].featuredImage
                                                ?.alt || ''
                                        }
                                        image={
                                            posts[currentSlide].featuredImage
                                                .gatsbyImageData
                                        }
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </Col>
                    <Col
                        offset={{ lg: 1 }}
                        col={{ lg: 6 }}
                        order={{ lg: 1 }}
                        pt={{ lg: 10 }}
                        pb={{ lg: 10 }}>
                        <Text
                            tag="p"
                            className={`subtitle mb-3 mb-lg-2 font-weight-bold text-uppercase ${style.title}`}>
                            {translation['studycases-section.pretitle'].text}
                        </Text>
                        <div className={style['slider-wrapper']}>
                            {posts?.length ? (
                                <Slider
                                    settings={sliderSettings}
                                    parentRef={sliderRef}>
                                    {posts.map((post, i) => (
                                        <div
                                            className={style['slider-slide']}
                                            key={i}>
                                            <TransitionLink
                                                to={post.slug}
                                                {...className(
                                                    'h1 mb-3 d-block',
                                                )}
                                                title={post.title}>
                                                {post.title}
                                            </TransitionLink>
                                            <ButtonComponent
                                                dark={dark}
                                                button={{
                                                    label: translation[
                                                        'studycases-section.study-case.cta'
                                                    ].text,
                                                    link: {
                                                        slug: post.slug,
                                                    },
                                                    type: 'secondary',
                                                }}
                                            />
                                        </div>
                                    ))}
                                </Slider>
                            ) : null}
                        </div>
                        <div
                            {...className(
                                `${style['thumbs-wrapper']} mt-3 mt-lg-10`,
                            )}>
                            <div
                                {...className(`d-flex ${style.thumbs}`)}
                                id="thumbnails-cos">
                                {posts?.length
                                    ? posts.map((p, i) => (
                                          <div
                                              key={i}
                                              {...className(
                                                  `${style['slider-thumb']}`,
                                              )}></div>
                                      ))
                                    : null}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

export const query = graphql`
    fragment CaseStudyItem on DatoCmsStudyCase {
        title
        slug
        featuredImage {
            alt
            gatsbyImageData(
                placeholder: BLURRED
                layout: FIXED
                imgixParams: { w: "880", h: "1232", fit: "crop" }
            )
        }
    }

    fragment DatoCmsCaseStudiesSection on DatoCmsCaseStudiesSection {
        dark
        posts {
            ...CaseStudyItem
        }
    }
`;
